import { ABSOLUTE_START_DISTANCE } from './constants';

export interface ScrollMetrics {
  scrollTop: number;
  maxScroll: number; // Maximum scroll position (scrollHeight - viewportHeight)
  viewportHeight: number;
}

/**
 * Extracts scroll metrics from a scroll target (Window or HTMLElement)
 */
export function getScrollMetrics(
  scrollTarget: HTMLElement | Window
): ScrollMetrics {
  const isWindow = scrollTarget === window;
  const element = scrollTarget as HTMLElement;

  const scrollTop = isWindow ? window.scrollY : element.scrollTop;
  const scrollHeight = isWindow
    ? document.documentElement.scrollHeight
    : element.scrollHeight;
  const viewportHeight = isWindow ? window.innerHeight : element.clientHeight;

  return {
    scrollTop,
    maxScroll: scrollHeight - viewportHeight,
    viewportHeight
  };
}

/**
 * Calculates scroll progress for parallax effect
 *
 * Returns a value between 0 and 1 representing how much the footer should be revealed:
 * - 0: Footer is hidden (before or at the reveal boundary)
 * - 1: Footer is fully revealed (at the bottom of the scroll)
 *
 * The reveal boundary is min(400px, half of max scroll distance) from the bottom.
 * The footer reveals linearly from this boundary to the bottom.
 */
export function calculateScrollProgress(metrics: ScrollMetrics): number {
  const { scrollTop, maxScroll } = metrics;

  // Page is not scrollable
  if (maxScroll <= 0) {
    return 0;
  }

  // Calculate the reveal boundary: min(400px, half of max scroll distance)
  const revealBoundary = Math.min(ABSOLUTE_START_DISTANCE, maxScroll / 2);

  // Calculate distance from bottom
  const distanceFromBottom = maxScroll - scrollTop;

  // If we're beyond the boundary (further from bottom), don't reveal
  if (distanceFromBottom >= revealBoundary) {
    return 0;
  }

  // Calculate linear progress from boundary (0) to bottom (1)
  const progress = 1 - distanceFromBottom / revealBoundary;

  return Math.min(Math.max(progress, 0), 1);
}

/**
 * Checks if a scroll target is currently scrollable
 */
export function isScrollable(scrollTarget: HTMLElement | Window): boolean {
  const { maxScroll } = getScrollMetrics(scrollTarget);
  return maxScroll > 0;
}
